"use client";

import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Download } from "lucide-react";
import type { SalesProject, WorkflowStage } from "@/types";

// PDF Styles - Matching the preview exactly
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 24,
    fontFamily: 'Helvetica',
  },
  section: {
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    marginBottom: 4,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  client: {
    fontSize: 10,
    marginBottom: 16,
    color: '#FF6900',
  },
  subtitle: {
    fontSize: 12,
    marginBottom: 12,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  label: {
    fontSize: 10,
    marginBottom: 2,
    color: '#6B7280',
  },
  value: {
    fontSize: 10,
    marginBottom: 8,
    color: '#1F2937',
  },
  valueBold: {
    fontSize: 10,
    marginBottom: 8,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  gridItem: {
    width: '50%',
    marginBottom: 8,
  },
  currentStage: {
    border: '1pt solid #D1D5DB',
    borderRadius: 4,
    padding: 12,
    marginBottom: 16,
  },
  stageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  stageDot: {
    width: 4,
    height: 4,
    backgroundColor: '#FF6900',
    borderRadius: 2,
    marginRight: 6,
  },
  stageTitle: {
    fontSize: 10,
    fontWeight: 'semibold',
    color: '#1F2937',
    flex: 1,
  },
  stageTimeline: {
    fontSize: 10,
    color: '#FF6900',
  },
  stageDescription: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 8,
  },
  stageGrid: {
    flexDirection: 'column',
    gap: 8,
  },
  footer: {
    borderTop: '1pt solid #E5E7EB',
    paddingTop: 16,
    marginTop: 'auto',
  },
  footerText: {
    fontSize: 8,
    color: '#6B7280',
  },
});

// PDF Document Component
const ProjectPDFDocument = ({ project, currentStage }: { project: SalesProject; currentStage: WorkflowStage | undefined }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.section}>
        <Text style={styles.title}>{project.name}</Text>
        <Text style={styles.client}>{project.client}</Text>
      </View>

      {/* Project Details */}
      <View style={styles.section}>
        <View style={styles.grid}>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Start Date</Text>
            <Text style={styles.value}>{project.start_date}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Expected Completion</Text>
            <Text style={styles.value}>{project.expected_completion}</Text>
          </View>
        </View>
      </View>

      {/* Progress Overview */}
      <View style={styles.section}>
        <View style={[styles.grid, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
          <Text style={styles.subtitle}>Overall Progress</Text>
          <Text style={styles.valueBold}>{project.progress}%</Text>
        </View>
      </View>

      {/* Current Stage */}
      {currentStage && (
        <View style={styles.currentStage}>
          <Text style={styles.subtitle}>Current Stage</Text>
          
          <View style={styles.stageHeader}>
            <View style={styles.stageDot} />
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', flex: 1, alignItems: 'center' }}>
              <Text style={styles.stageTitle}>
                Stage {currentStage.stage_number}: {currentStage.name}
              </Text>
              <Text style={styles.stageTimeline}>
                {currentStage.timeline}
              </Text>
            </View>
          </View>

          <Text style={styles.stageDescription}>
            {currentStage.description}
          </Text>

          <View style={styles.stageGrid}>
            <View>
              <Text style={styles.label}>Stage Owner</Text>
              <Text style={styles.value}>{currentStage.owner}</Text>
            </View>
            <View>
              <Text style={styles.label}>Rationale</Text>
              <Text style={styles.value}>{currentStage.rationale}</Text>
            </View>
          </View>
        </View>
      )}

      {/* Footer */}
      <View style={styles.footer}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text style={styles.footerText}>Generated on {new Date().toLocaleDateString()}</Text>
          <Text style={styles.footerText}>Project Management System</Text>
        </View>
      </View>
    </Page>
  </Document>
);

interface ProjectPDFProps {
  project: SalesProject;
  currentStage: WorkflowStage | undefined;
}

const ProjectPDF = ({ project, currentStage }: ProjectPDFProps) => {
  return (
    <PDFDownloadLink
      document={<ProjectPDFDocument project={project} currentStage={currentStage} />}
      fileName={`project-${project.name.replace(/\s+/g, '-')}.pdf`}
      className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded text-xs transition-colors"
    >
      {({ loading }) => (
        <>
          <Download className="w-4 h-4" />
          {loading ? 'Preparing PDF...' : 'Download PDF'}
        </>
      )}
    </PDFDownloadLink>
  );
};

export default ProjectPDF;